import React, { useEffect, useState } from "react";
import { TrendingUp, Users, BookOpenText, Award } from "lucide-react";
import { getSchoolPerformance } from "../../utils/admin";
import { mergeSubjectPerformance } from "../../utils/subjectDisplay";

const SectionHeader = ({ children, accent = "from-[#2148C0] to-blue-400" }) => (
    <h3 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center">
        <div className={`w-2 h-7 md:h-8 bg-gradient-to-b ${accent} rounded-full mr-3`} />
        {children}
    </h3>
);

const StatCard = ({ icon: Icon, label, value }) => (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
        <div className="flex items-center justify-between mb-3">
            <div className="p-2.5 rounded-xl bg-blue-50 text-[#2148C0]"><Icon size={20} /></div>
        </div>
        <p className="text-2xl font-bold text-gray-800">{value}</p>
        <p className="text-sm text-gray-500">{label}</p>
    </div>
);

const SchoolPerformancePage = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [yearGroup, setYearGroup] = useState("");
    const [days, setDays] = useState("30");

    const load = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("token");
            const params = {};
            if (yearGroup) params.yearGroup = yearGroup;
            if (days) params.days = days;
            const res = await getSchoolPerformance(token, params);
            setData(res?.data || null);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, [yearGroup, days]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <div className="space-y-8">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <SectionHeader>School Performance</SectionHeader>
                <div className="flex gap-2">
                    <input
                        placeholder="Filter by class (e.g. SS2)"
                        value={yearGroup}
                        onChange={(e) => setYearGroup(e.target.value)}
                        className="border border-gray-200 rounded-lg px-3 py-2 text-sm"
                    />
                    <select value={days} onChange={(e) => setDays(e.target.value)} className="border border-gray-200 rounded-lg px-3 py-2 text-sm">
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="">All time</option>
                    </select>
                </div>
            </div>

            {loading && <p className="text-gray-500">Loading...</p>}

            {!loading && data && (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                        <StatCard icon={Users} label="Students in scope" value={data.totalStudents} />
                        <StatCard icon={TrendingUp} label="Overall accuracy" value={`${data.overallAccuracy}%`} />
                        <StatCard icon={BookOpenText} label="Active students" value={`${data.engagement.activeStudents}/${data.engagement.totalStudents}`} />
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-700 mb-3">Subject Performance</h4>
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y">
                            {mergeSubjectPerformance(data.subjectPerformance).length === 0 && (
                                <p className="text-sm text-gray-500 p-4">No practice data for this filter yet.</p>
                            )}
                            {mergeSubjectPerformance(data.subjectPerformance).map((s) => (
                                <div key={s.subject} className="p-4 flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-800">{s.subject}</p>
                                        <p className="text-xs text-gray-400">{s.attempts} attempts</p>
                                    </div>
                                    <span className={`font-bold ${s.accuracy >= 70 ? "text-green-600" : s.accuracy >= 50 ? "text-yellow-600" : "text-red-500"}`}>
                                        {s.accuracy}%
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div>
                        <h4 className="font-semibold text-gray-700 mb-3 flex items-center gap-2"><Award size={16} /> Teacher Impact</h4>
                        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm divide-y">
                            {data.teacherImpact.length === 0 && (
                                <p className="text-sm text-gray-500 p-4">No linked teacher/student activity for this filter yet.</p>
                            )}
                            {data.teacherImpact.map((t) => (
                                <div key={t.teacher_id} className="p-4 flex items-center justify-between">
                                    <div>
                                        <p className="font-medium text-gray-800">{t.name}</p>
                                        <p className="text-xs text-gray-400">{t.studentCount} student(s)</p>
                                    </div>
                                    <span className="font-bold text-[#2148C0]">
                                        {t.averageAccuracy != null ? `${t.averageAccuracy}%` : "—"}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default SchoolPerformancePage;
